import axios from 'axios';
import { API_BASE_URL } from '@config';
import * as ActionsDialog from 'app/store/actions';
import { showMessage } from 'app/store/actions/fuse';

// Feature: 001-org-multi-tenancy - Organizations Redux Actions (T029)

// SET Actions
export const SET_ORGANIZATIONS_LIST = 'SET_ORGANIZATIONS_LIST';
export const setOrganizationsList = organizations => ({
	type: SET_ORGANIZATIONS_LIST,
	payload: organizations
});

export const SET_ORGANIZATION = 'SET_ORGANIZATION';
export const setOrganization = organization => ({
	type: SET_ORGANIZATION,
	payload: organization
});

export const SET_SELECTED_ORGANIZATION = 'SET_SELECTED_ORGANIZATION';
export const setSelectedOrganization = organization => ({
	type: SET_SELECTED_ORGANIZATION,
	payload: organization
});

export const SET_ORGANIZATION_USERS = 'SET_ORGANIZATION_USERS';
export const setOrganizationUsers = users => ({
	type: SET_ORGANIZATION_USERS,
	payload: users
});

export const SET_ORGANIZATION_ERROR = 'SET_ORGANIZATION_ERROR';
export const setOrganizationError = error => ({
	type: SET_ORGANIZATION_ERROR,
	payload: error
});

export const SET_ORGANIZATION_LOADING = 'SET_ORGANIZATION_LOADING';
export const setOrganizationLoading = loading => ({
	type: SET_ORGANIZATION_LOADING,
	payload: loading
});

export const SET_ORGANIZATIONS_SEARCH_TEXT = 'SET_ORGANIZATIONS_SEARCH_TEXT';
export const setOrganizationsSearchText = event => ({
	type: SET_ORGANIZATIONS_SEARCH_TEXT,
	searchText: event.target.value
});

export const SET_ORGANIZATION_MODE_ENABLED = 'SET_ORGANIZATION_MODE_ENABLED';
export const setOrganizationModeEnabled = enabled => ({
	type: SET_ORGANIZATION_MODE_ENABLED,
	payload: enabled
});

export const SET_MY_ORGANIZATION = 'SET_MY_ORGANIZATION';
export const setMyOrganization = organization => ({
	type: SET_MY_ORGANIZATION,
	payload: organization
});

// CLEAR Actions
export const CLEAR_ORGANIZATION = 'CLEAR_ORGANIZATION';
export const clearOrganization = () => ({
	type: CLEAR_ORGANIZATION
});

export const CLEAR_ORGANIZATION_ERROR = 'CLEAR_ORGANIZATION_ERROR';
export const clearOrganizationError = () => ({
	type: CLEAR_ORGANIZATION_ERROR
});

export const CLEAR_ORGANIZATIONS_REDUCER = 'CLEAR_ORGANIZATIONS_REDUCER';
export const clearOrganizationsReducer = () => ({
	type: CLEAR_ORGANIZATIONS_REDUCER
});

// * ////////////// HEADERS ////////////// * //
const headers = {
	headers: {
		admin: true
	}
};

// * ////////////// NEW ////////////// * //

export const newOrganization = () => dispatch => {
	const organization = {
		name: '',
		description: '',
		status: 'active',
		license: {
			maxUsers: 10,
			expiresAt: null,
			features: []
		},
		settings: {
			allowedProviders: [],
			maxClusters: null,
			maxTrainings: null
		},
		contactEmail: '',
		contactName: ''
	};
	dispatch(setOrganization(organization));
};

// * ////////////// GET ////////////// * //

export const getOrganizations = (showLoadingIndicator = true) => dispatch => {
	if (showLoadingIndicator) dispatch(setOrganizationLoading(true));
	const request = axios.get(`${API_BASE_URL}/api/organizations/`, headers);
	return request
		.then(response => {
			// API returns { organizations: [...], total, page, limit }
			dispatch(setOrganizationsList(response.data.organizations || []));
			if (showLoadingIndicator) dispatch(setOrganizationLoading(false));
		})
		.catch(err => {
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			if (showLoadingIndicator) dispatch(setOrganizationLoading(false));
		});
};

export const getOrganization = (id, showLoadingIndicator = true) => dispatch => {
	if (showLoadingIndicator) dispatch(setOrganizationLoading(true));
	const request = axios.get(`${API_BASE_URL}/api/organizations/${id}`, headers);
	return request
		.then(response => {
			dispatch(setOrganization(response.data));
			if (showLoadingIndicator) dispatch(setOrganizationLoading(false));
			return response.data;
		})
		.catch(err => {
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			if (showLoadingIndicator) dispatch(setOrganizationLoading(false));
			throw err;
		});
};

export const getMyOrganization = () => dispatch => {
	dispatch(setOrganizationLoading(true));
	const request = axios.get(`${API_BASE_URL}/api/organizations/my`);
	return request
		.then(response => {
			dispatch(setMyOrganization(response.data));
			dispatch(setOrganizationLoading(false));
			return response.data;
		})
		.catch(err => {
			// 404 means user is not part of any organization - not an error
			if (err.response?.status === 404) {
				dispatch(setMyOrganization(null));
			} else {
				dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			}
			dispatch(setOrganizationLoading(false));
		});
};

export const getOrganizationUsers = (id) => dispatch => {
	dispatch(setOrganizationLoading(true));
	const request = axios.get(`${API_BASE_URL}/api/organizations/${id}/users`, headers);
	return request
		.then(response => {
			// API returns { users: [...], total }
			dispatch(setOrganizationUsers(response.data.users || []));
			dispatch(setOrganizationLoading(false));
			return response.data.users;
		})
		.catch(err => {
			console.error('[getOrganizationUsers action] Error:', err.response?.data || err.message);
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			dispatch(setOrganizationLoading(false));
			throw err;
		});
};

export const getAvailableUsers = () => {
	const request = axios.get(`${API_BASE_URL}/api/organizations/available-users`, headers);
	return request
		.then(response => {
			return response.data.users || [];
		})
		.catch(err => {
			console.error('Error fetching available users:', err);
			return [];
		});
};

export const checkOrganizationModeEnabled = () => dispatch => {
	const request = axios.get(`${API_BASE_URL}/api/settings/organization-mode`);
	return request
		.then(response => {
			dispatch(setOrganizationModeEnabled(response.data.enabled));
			return response.data.enabled;
		})
		.catch(err => {
			console.error('Error checking organization mode:', err);
			dispatch(setOrganizationModeEnabled(false));
			return false;
		});
};

// * ////////////// POST ////////////// * //

export const createOrganization = (organizationData) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.post(`${API_BASE_URL}/api/organizations/`, organizationData, headers);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(ActionsDialog.closeDialog());
			dispatch(getOrganizations());
			dispatch(showMessage({ message: 'Organization created successfully' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'form' }));
			throw err;
		});
};

export const addUserToOrganization = (organizationId, userId, isOrganizationAdmin = false) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.post(
		`${API_BASE_URL}/api/organizations/${organizationId}/users`,
		{ userId, isOrganizationAdmin },
		headers
	);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getOrganizationUsers(organizationId));
			dispatch(showMessage({ message: 'User added to organization' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			throw err;
		});
};

export const createUserInOrganization = (organizationId, userData) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.post(
		`${API_BASE_URL}/api/organizations/${organizationId}/users/create`,
		userData,
		headers
	);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getOrganizationUsers(organizationId));
			dispatch(showMessage({ message: 'User created successfully' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'form' }));
			throw err;
		});
};

// * ////////////// PUT ////////////// * //

export const updateOrganization = (id, organizationData) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/organizations/${id}`, organizationData, headers);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(ActionsDialog.closeDialog());
			dispatch(getOrganizations());
			dispatch(showMessage({ message: 'Organization updated successfully' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'form' }));
			throw err;
		});
};

export const updateOrganizationStatus = (id, status) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/organizations/${id}/status`, { status }, headers);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getOrganizations());
			dispatch(showMessage({ message: `Organization ${status === 'active' ? 'activated' : status}` }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			throw err;
		});
};

export const updateOrganizationLicense = (id, licenseData) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/organizations/${id}/license`, licenseData, headers);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getOrganization(id));
			dispatch(showMessage({ message: 'Organization license updated' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'form' }));
			throw err;
		});
};

export const removeUserFromOrganization = (organizationId, userId) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.delete(
		`${API_BASE_URL}/api/organizations/${organizationId}/users/${userId}`,
		headers
	);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getOrganizationUsers(organizationId));
			dispatch(showMessage({ message: 'User removed from organization' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			throw err;
		});
};

export const setUserAsOrgAdmin = (organizationId, userId, isAdmin) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());

	// Use different endpoints for promote vs demote
	const request = isAdmin
		? axios.post(`${API_BASE_URL}/api/organizations/${organizationId}/admins`, { userId }, headers)
		: axios.delete(`${API_BASE_URL}/api/organizations/${organizationId}/admins/${userId}`, headers);

	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getOrganizationUsers(organizationId));
			dispatch(showMessage({ message: isAdmin ? 'User promoted to organization admin' : 'User demoted from organization admin' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			throw err;
		});
};

// * ////////////// DELETE ////////////// * //

export const deleteOrganization = (id) => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.delete(`${API_BASE_URL}/api/organizations/${id}`, headers);
	return request
		.then(() => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(getOrganizations());
			dispatch(showMessage({ message: 'Organization deleted' }));
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			throw err;
		});
};

// * ////////////// ORGANIZATION MODE TOGGLE ////////////// * //

export const enableOrganizationMode = () => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/settings/organization-mode`, { enabled: true }, headers);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationModeEnabled(true));
			dispatch(showMessage({ message: 'Organization mode enabled' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			throw err;
		});
};

export const disableOrganizationMode = () => dispatch => {
	dispatch(ActionsDialog.toggleLoading());
	const request = axios.put(`${API_BASE_URL}/api/settings/organization-mode`, { enabled: false, confirmation: 'DISABLE' }, headers);
	return request
		.then(response => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationModeEnabled(false));
			dispatch(showMessage({ message: 'Organization mode disabled' }));
			return response.data;
		})
		.catch(err => {
			dispatch(ActionsDialog.toggleLoading());
			dispatch(setOrganizationError({ ...err.response?.data, type: 'modal' }));
			throw err;
		});
};
